import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  ArrowLeft,
  Download,
  CreditCard,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  DollarSign,
  Mail,
  Phone,
  Shield,
  Lock,
  Smartphone,
  MessageCircle,
  Loader2
} from 'lucide-react'
import { invoiceService, clientService, settingsService, notificationService } from '../services/firebase'
import { downloadInvoicePDF, downloadReceiptPDF } from '../services/pdfService'
import { ncinqApiService } from '../services/ncinqApiService'

export default function ClientPortal() {
  const { invoiceId } = useParams()

  // Local state for public access
  const [invoice, setInvoice] = useState(null)
  const [client, setClient] = useState(null)
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notification, setNotification] = useState(null)

  const [showPaymentModal, setShowPaymentModal] = useState(false)

  // Fetch data on load
  useEffect(() => {
    const loadPublicData = async () => {
      try {
        setLoading(true)
        // 1. Get Invoice
        const inv = await invoiceService.getPublicInvoice(invoiceId)
        if (!inv) {
          setError('Invoice not found')
          setLoading(false)
          return
        }
        setInvoice(inv)

        // 2. Get Settings (Business Info)
        if (inv.userId) {
          const sett = await settingsService.getPublicSettings(inv.userId)
          setSettings(sett)
        }

        // 3. Get Client
        if (inv.clientId) {
          const cli = await clientService.getPublicClient(inv.clientId)
          setClient(cli)
        }

        // 4. Record View (Read Receipt)
        // Only if status is 'sent' to avoid spamming updates
        if (inv.status === 'sent') {
          // Fire and forget - don't await this to block rendering
          invoiceService.markAsViewed(inv.id, inv.userId).catch(console.error)
        }
      } catch (err) {
        console.error('Error loading public invoice:', err)
        setError('Failed to load invoice details')
      } finally {
        setLoading(false)
      }
    }

    if (invoiceId) {
      loadPublicData()
    }
  }, [invoiceId])

  const showNotification = (message, type = 'success') => {
    const id = Date.now()
    setNotification({ id, message, type })
    setTimeout(() => setNotification(null), 4000)
  }

  const handleDownloadPDF = async () => {
    try {
      await downloadInvoicePDF(invoice, client, settings)
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      showNotification('Failed to download PDF', 'error')
    }
  }

  const handleDownloadReceipt = async () => {
    try {
      const lastPaymentAmount = invoice.payments && invoice.payments.length > 0
        ? invoice.payments[invoice.payments.length - 1].amount
        : invoice.partialAmount || invoice.total

      await downloadReceiptPDF(invoice, client, settings, lastPaymentAmount)
    } catch (error) {
      console.error('Failed to generate Receipt:', error)
      showNotification('Failed to download Receipt', 'error')
    }
  }

  // Handle Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 size={40} className="animate-spin text-primary-600" />
      </div>
    )
  }

  // Handle Error State
  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Unavailable</h1>
          <p className="text-gray-500">{error || "The invoice you're looking for doesn't exist."}</p>
        </div>
      </div>
    )
  }

  const getStatusConfig = (status) => {
    const configs = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-700', icon: FileText, label: 'Draft' },
      sent: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Pending' },
      viewed: { bg: 'bg-indigo-100', text: 'text-indigo-700', icon: Clock, label: 'Pending' },
      partial: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock, label: 'Partially Paid' },
      paid: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Paid' },
      overdue: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle, label: 'Overdue' },
    }
    return configs[status] || configs.draft
  }

  const statusConfig = getStatusConfig(invoice.status)
  const StatusIcon = statusConfig.icon
  const isOverdue = invoice.status === 'overdue' || (invoice.status !== 'paid' && new Date(invoice.dueDate) < new Date())
  const balanceDue = invoice.total - (invoice.partialAmount || 0)

  // Payment Success Handler
  const handlePaymentSuccess = async (reference) => {
    try {
      await invoiceService.recordPublicPayment(invoice.id, {
        amount: balanceDue,
        method: 'mmg',
        reference
      })

      // Update local state to reflect payment immediately
      const newPartial = (invoice.partialAmount || 0) + balanceDue
      const newStatus = newPartial >= invoice.total ? 'paid' : 'partial'

      setInvoice(prev => ({
        ...prev,
        status: newStatus,
        partialAmount: newPartial,
        paidAt: new Date().toISOString() // Optimistic update
      }))

      showNotification('Payment recorded! We will verify the transaction shortly.', 'success')
      setShowPaymentModal(false)
    } catch (err) {
      console.error('Payment record error:', err)
      showNotification('Payment processed but failed to record. Please contact support.', 'error')
    }
  }

  const getWhatsAppLink = (phone) => {
    if (!phone) return ''
    const sanitized = phone.replace(/\D/g, '')
    return `https://wa.me/${sanitized}`
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>
          {invoice
            ? `${settings?.businessName || 'Invoice'} - ${invoice.subject ? invoice.subject : invoice.invoiceNumber}`
            : 'Secure Invoice Portal'}
        </title>
        <meta
          name="description"
          content={invoice
            ? `View invoice ${invoice.invoiceNumber} from ${settings?.businessName || 'Business'}. Secure payment portal.`
            : 'Access your secure invoice portal.'}
        />
        {/* Open Graph / WhatsApp Preview */}
        <meta property="og:title" content={`${settings?.businessName || 'Invoice'} sent you an invoice`} />
        <meta property="og:description" content={invoice ? `Invoice #${invoice.invoiceNumber} due on ${new Date(invoice.dueDate).toLocaleDateString()}` : 'View your invoice details securely.'} />
        {/* If settings.logo is a URL (not base64), use it. Otherwise use default or empty */}
        {settings?.logo && !settings.logo.startsWith('data:') && (
          <meta property="og:image" content={settings.logo} />
        )}
      </Helmet>

      {/* Toast Notification */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-slide-up">
          <div className={`px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 ${notification.type === 'error' ? 'bg-white border-red-100' : 'bg-white border-green-100'
            }`}>
            {notification.type === 'error' ? (
              <AlertCircle size={18} className="text-red-500" />
            ) : (
              <CheckCircle2 size={18} className="text-green-500" />
            )}
            <span className="text-sm text-gray-700">{notification.message}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings?.logo ? (
              <img src={settings.logo} alt={settings.businessName} className="h-10 w-auto object-contain" />
            ) : (
              <img src="/ncinq-favicon.png" alt="nCinq Invoice" className="h-10 w-auto" />
            )}
            <div>
              <h1 className="font-bold text-gray-900">{settings?.businessName || 'nCinq Invoice'}</h1>
              <p className="text-xs text-gray-500">Secure Client Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {invoice?.clientId && (
              <Link
                to={`/portal/client/${invoice.clientId}`}
                className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-primary-600 transition-colors"
              >
                <ArrowLeft size={16} />
                Back to Dashboard
              </Link>
            )}
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Lock size={14} />
              <span>Secure Payment</span>
            </div>
          </div>
        </div>
      </header >

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice Details Container */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dedicated Action Bar */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-3 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                <button
                  onClick={handleDownloadPDF}
                  className="btn btn-secondary text-xs px-3 py-1.5 h-9 sm:h-auto"
                >
                  <Download size={14} />
                  <span className="whitespace-nowrap">Download PDF</span>
                </button>
                {['partial', 'paid'].includes(invoice.status) && (
                  <button
                    onClick={handleDownloadReceipt}
                    className="btn btn-secondary text-xs px-3 py-1.5 h-9 sm:h-auto"
                  >
                    <FileText size={14} />
                    <span className="whitespace-nowrap">Download Receipt</span>
                  </button>
                )}
                {['partial', 'paid'].includes(invoice.status) && (
                  <Link
                    to={`/portal/${invoice.id}/receipt`}
                    className="btn btn-primary text-xs px-3 py-1.5 h-9 sm:h-auto"
                  >
                    <CheckCircle2 size={14} />
                    <span className="whitespace-nowrap">View Web Receipt</span>
                  </Link>
                )}
              </div>
            </div>

            {/* Invoice Card */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              {/* Invoice Header */}
              <div className="p-8 border-b border-gray-100">
                <div className="mb-6">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <h2 className="text-3xl font-bold text-gray-900 tracking-tight">INVOICE</h2>
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusConfig.bg} ${statusConfig.text}`}>
                      <StatusIcon size={14} />
                      {statusConfig.label}
                    </span>
                    {isOverdue && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-700 text-xs font-bold uppercase tracking-wider">
                        Overdue
                      </span>
                    )}
                  </div>
                  <p className="text-xl text-gray-600 mb-2 font-medium">{invoice.invoiceNumber}</p>
                  {invoice.subject && (
                    <h3 className="text-2xl font-bold text-gray-800 leading-tight">{invoice.subject}</h3>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-8">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Issued By</p>
                    <p className="font-semibold text-gray-900">{settings?.businessName || 'Company Name'}</p>
                    <p className="text-sm text-gray-600 whitespace-pre-line mt-1">{settings?.businessAddress}</p>
                    <p className="text-sm text-gray-600">{settings?.businessEmail}</p>
                  </div>
                  <div className="text-right">
                    <div className="mb-3">
                      <p className="text-sm text-gray-500">Issue Date</p>
                      <p className="font-medium text-gray-900">
                        {new Date(invoice.issueDate).toLocaleDateString('en-US', {
                          month: 'long', day: 'numeric', year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className={isOverdue ? 'text-red-600' : ''}>
                      <p className="text-sm text-gray-500">Due Date</p>
                      <p className="font-medium">
                        {new Date(invoice.dueDate).toLocaleDateString('en-US', {
                          month: 'long', day: 'numeric', year: 'numeric'
                        })}
                        {isOverdue && (
                          <span className="ml-2 text-red-600 font-semibold">(Overdue)</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bill To */}
              <div className="p-8 bg-gray-50 border-b border-gray-100">
                <p className="text-sm text-gray-500 mb-1 font-medium">Bill To</p>
                {client?.businessName && (
                  <p className="font-bold text-gray-900 text-lg">{client.businessName}</p>
                )}
                <p className={`${client?.businessName ? 'font-medium text-gray-700' : 'font-semibold text-gray-900 text-lg'}`}>
                  {client?.name || 'Valued Client'}
                </p>
                {client?.address && (
                  <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">
                    {client.address}
                  </p>
                )}
                <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Mail size={12} />
                    {client?.email}
                  </span>
                  {client?.phone && (
                    <span className="flex items-center gap-1">
                      <Phone size={12} />
                      {client.phone}
                    </span>
                  )}
                </div>
              </div>

              {/* Line Items */}
              <div className="p-8">
                <table className="w-full mb-8">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 text-sm font-semibold text-gray-600">Description</th>
                      <th className="text-center py-3 text-sm font-semibold text-gray-600 w-24">Qty</th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-600 w-28">Rate</th>
                      <th className="text-right py-3 text-sm font-semibold text-gray-600 w-28">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100">
                        <td className="py-4 text-gray-900">{item.description}</td>
                        <td className="py-4 text-center text-gray-600">{item.quantity}</td>
                        <td className="py-4 text-right text-gray-600">${item.rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="py-4 text-right font-medium text-gray-900">
                          ${(item.quantity * item.rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="flex justify-end mb-8">
                  <div className="w-64 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="text-gray-900">${invoice.subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {invoice.taxRate > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{invoice.taxLabel} ({invoice.taxRate}%)</span>
                        <span className="text-gray-900">${invoice.taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    {invoice.discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Discount</span>
                        <span className="text-green-600">-${invoice.discount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold border-t-2 border-gray-200 pt-3">
                      <span>Total</span>
                      <span className="text-primary-600">${invoice.total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {settings?.enableExchange && settings?.exchangeRate > 0 && (
                      <div className="flex justify-between text-sm italic text-gray-500 pt-1">
                        <span>Equivalent in {settings.exchangeCurrency || 'GYD'}</span>
                        <span>
                          {(invoice.total * settings.exchangeRate).toLocaleString('en-US', {
                            style: 'currency',
                            currency: settings.exchangeCurrency || 'GYD'
                          })}
                        </span>
                      </div>
                    )}
                    {invoice.partialAmount > 0 && (
                      <>
                        {invoice.payments && invoice.payments.length > 0 ? (
                          <div className="pt-3 border-t border-gray-100 space-y-2">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Payment History</p>
                            {invoice.payments && [...invoice.payments].sort((a, b) => {
                              const dateA = a.date && (a.date.toDate ? a.date.toDate() : new Date(a.date))
                              const dateB = b.date && (b.date.toDate ? b.date.toDate() : new Date(b.date))
                              return dateB - dateA
                            }).map((payment, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span className="text-gray-500">
                                  Paid on {payment.date && (payment.date.toDate ? payment.date.toDate() : new Date(payment.date)).toLocaleDateString()}
                                </span>
                                <span className="text-green-600 font-medium">-${Number(payment.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </div>
                            ))}
                            <div className="flex justify-between text-sm font-semibold pt-2 border-t border-gray-50">
                              <span className="text-gray-700">Total Paid</span>
                              <span className="text-green-600">-${invoice.partialAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
                            <span className="text-gray-500">Paid</span>
                            <span className="text-green-600">-${invoice.partialAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-lg font-bold border-t-2 border-gray-200 pt-3">
                          <span>Balance Due</span>
                          <span className="text-primary-600">${balanceDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {invoice.notes && (
                  <div className="border-t border-gray-200 pt-6">
                    <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                    <p className="text-sm text-gray-600">{invoice.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Payment Sidebar */}
          <div className="space-y-6">
            {/* Payment Card */}
            {invoice.status !== 'paid' && (
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                    <DollarSign size={24} className="text-accent-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Amount Due</h3>
                    <p className="text-2xl font-bold text-primary-600">
                      ${balanceDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>

                {isOverdue && (
                  <div className="mb-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                      <AlertCircle size={16} />
                      This invoice is overdue
                    </p>
                  </div>
                )}

                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="btn btn-primary w-full py-3 text-lg bg-yellow-400 hover:bg-yellow-500 text-gray-900 border-none"
                >
                  <CreditCard size={20} />
                  Pay with MMG
                </button>

                <p className="text-xs text-gray-500 text-center mt-4 flex items-center justify-center gap-1">
                  <Shield size={12} />
                  Secure payment via MMG
                </p>
              </div>
            )}

            {invoice.status === 'paid' && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                    <CheckCircle2 size={24} className="text-accent-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Invoice Paid</h3>
                    <p className="text-sm text-gray-500">
                      Thank you for your payment!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Business Info */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Questions?</h3>
              <div className="space-y-3">
                <a
                  href={`mailto:${settings?.businessEmail}`}
                  className="flex items-center gap-3 text-sm text-gray-600 hover:text-primary-600 transition-colors"
                >
                  <Mail size={16} className="text-gray-400" />
                  {settings?.businessEmail}
                </a>
                {settings?.businessPhone && (
                  <a
                    href={getWhatsAppLink(settings.businessPhone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <MessageCircle size={16} className="text-gray-400" />
                    WhatsApp
                  </a>
                )}
              </div>
            </div>

            {/* Security Info */}
            <div className="bg-gray-900 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-3 mb-4 text-accent-400">
                <Shield size={24} />
                <h3 className="text-lg font-bold">Secure MMG Payment</h3>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Your payment is processed securely via MMG (Mobile Money Guyana). No sensitive bank or card details are required for mobile wallet transfers.
              </p>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700">
                  <Smartphone size={16} className="text-accent-400" />
                  <span className="text-xs font-bold tracking-wider">MOBILE WALLET</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700">
                  <Lock size={16} className="text-accent-400" />
                  <span className="text-xs font-bold tracking-wider">ENCRYPTED</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main >

      {/* Payment Modal */}
      {
        showPaymentModal && (
          <PaymentModal
            amount={balanceDue}
            invoice={invoice}
            onClose={() => setShowPaymentModal(false)}
          />
        )
      }
    </div >
  )
}

function PaymentModal({ amount, invoice, onClose }) {
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [showManual, setShowManual] = useState(false)

  const handleAutomatedCheckout = async () => {
    setSending(true)
    setError(null)
    try {
      const payload = {
        amount: Number(amount),
        description: invoice.subject ? `Invoice ${invoice.invoiceNumber}: ${invoice.subject}` : `Payment for Invoice ${invoice.invoiceNumber}`,
        customerName: invoice.clientName || 'Valued Client',
        customerEmail: invoice.clientEmail || '',
        invoiceId: invoice.id,
        redirectUrl: window.location.href, // Return to this page
      };

      const result = await ncinqApiService.initiateCheckout(payload);
      if (result && result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        throw new Error('Failed to initiate checkout - No URL returned');
      }
    } catch (err) {
      console.error('Checkout error:', err)
      setError('Technical issue initiating payment. Please try manual or contact support.')
      setSending(false)
    }
  }

  const handleManualSent = async () => {
    setSending(true)
    setError(null)
    try {
      // Notify the admin (manual flow)
      await notificationService.addNotification(invoice.userId, {
        message: `Client reported payment sent for ${invoice.invoiceNumber}`,
        type: 'success',
        link: `/invoices/${invoice.id}/view`
      })
      onClose()
    } catch (error) {
      console.error('Error sending notification', error)
      setError('Failed to send notification. Please try again.')
      setSending(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md animate-scale-in overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="pt-8 px-8 flex justify-between items-start">
          <div>
            <h3 className="text-2xl font-bold text-gray-900">Secure Payment</h3>
            <p className="text-gray-500 text-sm mt-1">Complete your transaction via MMG</p>
          </div>
          <button 
            onClick={onClose} 
            className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
          >
            <ArrowLeft size={20} />
          </button>
        </div>

        <div className="p-8">
          {/* Amount Display */}
          <div className="bg-primary-50/50 rounded-3xl p-6 text-center mb-8 border border-primary-100/50">
            <p className="text-xs font-bold text-primary-600 uppercase tracking-widest mb-1">Total Amount Due</p>
            <p className="text-4xl font-extrabold text-gray-900 tracking-tight">
              ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 text-sm animate-shake">
              <AlertCircle size={20} className="shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
          )}

          {/* Primary Action: Direct Checkout */}
          <div className="space-y-4">
            <button
              onClick={handleAutomatedCheckout}
              disabled={sending}
              className="group relative w-full py-4 px-6 bg-yellow-400 hover:bg-yellow-500 disabled:bg-gray-100 disabled:text-gray-400 text-gray-900 font-bold rounded-2xl shadow-lg shadow-yellow-400/20 active:scale-[0.98] transition-all flex items-center justify-center gap-3 overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              {sending ? (
                <>
                  <Loader2 size={24} className="animate-spin" />
                  <span>Connecting...</span>
                </>
              ) : (
                <>
                  <CreditCard size={22} />
                  <span className="text-lg">Initiate Direct Checkout</span>
                </>
              )}
            </button>
            <div className="flex items-center justify-center gap-2 text-xs text-gray-400 font-medium">
              <Shield size={14} className="text-green-500" />
              <span>Direct encryption with MMG Gateway</span>
            </div>
          </div>

          {/* Secondary Action: Manual Toggle */}
          <div className="mt-10 pt-6 border-t border-gray-50 text-center">
            <button 
              onClick={() => setShowManual(!showManual)}
              className="text-sm font-semibold text-gray-500 hover:text-primary-600 transition-colors inline-flex items-center gap-2"
            >
              {showManual ? 'Back to direct checkout' : 'Need to pay manually?'}
              <div className={`transition-transform duration-300 ${showManual ? 'rotate-180' : ''}`}>
                <ArrowLeft size={14} className="-rotate-90" />
              </div>
            </button>

            {showManual && (
              <div className="mt-8 animate-slide-up">
                <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col items-center">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Merchant Details</p>
                  <div className="w-full py-2 px-4 bg-white rounded-xl border border-gray-100 flex justify-between items-center mb-4">
                    <span className="text-xs text-gray-500 font-medium tracking-tight">MERCHANT ID</span>
                    <span className="font-mono font-bold text-gray-900">a820453</span>
                  </div>
                  <div className="relative group p-2 bg-white rounded-2xl border border-gray-100 shadow-sm mb-6">
                    <img src="/mmg-qr.png" alt="Merchant QR" className="w-32 h-32 group-hover:scale-105 transition-transform" />
                  </div>
                  <button
                    onClick={handleManualSent}
                    disabled={sending}
                    className="w-full py-3 px-6 bg-gray-900 hover:bg-gray-800 text-white font-bold rounded-xl active:scale-[0.98] transition-all"
                  >
                    {sending ? 'Processing...' : "I've Sent the Payment"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50/50 border-t border-gray-50 flex items-center justify-center gap-4">
          <div className="flex items-center gap-1.5 grayscale opacity-50">
             <Smartphone size={14} className="text-gray-400" />
             <span className="text-[10px] font-bold tracking-widest text-gray-400 uppercase">nCinq Verify</span>
          </div>
        </div>
      </div>
    </div>
  )
}
